import { FolderIcon } from "../atoms/FolderIcon";
import { DocumentCheckbox } from "../molecules/DocumentCheckbox";
import { DocumentActions } from "../molecules/DocumentActions";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { isDocumentProcessing } from "../../utils/documentState";
import { rowSelectionToggle } from "../../utils/rowSelectionClick";

interface ContainerFolderRowProps {
  /** Container filename (e.g., "archive.zip") */
  filename: string;
  /** Number of child documents inside the container */
  childCount?: number;
  /** Document processing state */
  state: string | null;
  /** Whether the row is selected */
  isSelected?: boolean;
  /** Show/hide Checkbox column */
  showCheckbox?: boolean;
  /** Show/hide Actions column */
  showActions?: boolean;
  /** Callback when checkbox is toggled */
  onSelect?: (isSelected: boolean) => void;
  /** Callback when the container row is clicked (navigate into) */
  onClick?: () => void;
  /** Callback when download is clicked */
  onDownload?: () => void;
  /** Callback when delete is clicked */
  onDelete?: () => void;
  /** Custom delete label */
  deleteLabel?: string;
  /** Render the destructive action as a non-destructive Restore action. Used in trash view. */
  isRestore?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ContainerFolderRow - Folder-like row for container files (.zip, .asice).
 *
 * Renders container documents as navigable folder rows with child count,
 * download/delete actions, and processing indicator.
 */
export function ContainerFolderRow({
  filename,
  childCount,
  state,
  isSelected = false,
  showCheckbox = true,
  showActions = true,
  onSelect,
  onClick,
  onDownload,
  onDelete,
  deleteLabel,
  isRestore = false,
  className,
}: ContainerFolderRowProps) {
  const isProcessing = state !== null && isDocumentProcessing(state);

  // Checkbox column width must match DocumentTableRow / DocumentListHeader so
  // the checkboxes line up vertically across folder and document rows.
  const gridColumns = [showCheckbox && "28px", "1fr", showActions && "50px"]
    .filter(Boolean)
    .join(" ");

  // Plain row click (anywhere except title, checkbox, or actions) toggles selection.
  // Cmd/Ctrl-click also toggles selection (kept for muscle memory).
  // Click on the title navigates into the container.
  const handleRowClick = rowSelectionToggle(isSelected, onSelect);

  const handleTitleClick = (e: React.MouseEvent) => {
    if (!onClick) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.stopPropagation();
    onClick();
  };

  return (
    <div
      className={cn(
        "group grid gap-4 border-b px-6 py-4 transition-colors",
        isSelected ? "border-primary/50 bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/50",
        className
      )}
      style={{ gridTemplateColumns: gridColumns }}
      onClick={handleRowClick}
    >
      {/* Checkbox */}
      {showCheckbox && (
        <DocumentCheckbox isSelected={isSelected} onSelect={(selected) => onSelect?.(selected)} />
      )}

      {/* Folder Icon + Filename + Child count */}
      <div className="flex min-w-0 gap-3">
        <div className="flex-shrink-0 pt-1">
          <FolderIcon isOpen={false} className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            data-row-title
            onClick={handleTitleClick}
            disabled={!onClick}
            className={cn(
              "block max-w-full truncate text-left text-sm font-medium",
              onClick && "cursor-pointer hover:text-primary hover:underline"
            )}
          >
            {filename}
          </button>
          {(isProcessing || childCount !== undefined) && (
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              {isProcessing ? (
                <span className="flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Extracting files...
                </span>
              ) : (
                <span>
                  {childCount} {childCount === 1 ? "document" : "documents"}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex justify-end" data-actions onClick={(e) => e.stopPropagation()}>
          <div className="opacity-0 transition-opacity group-hover:opacity-100">
            <DocumentActions
              onDownload={onDownload ?? (() => {})}
              onDelete={onDelete ?? (() => {})}
              showDownload={!!onDownload}
              showDelete={!!onDelete}
              deleteLabel={deleteLabel}
              isRestore={isRestore}
            />
          </div>
        </div>
      )}
    </div>
  );
}
